import {AfterViewInit, Component, OnDestroy, ViewChild} from '@angular/core';
import {MatTableDataSource} from "@angular/material/table";
import {PageEvent} from "@angular/material/paginator";
import {FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap} from "rxjs/operators";
import {UserService} from "../../../../@shared/services/user.service";
import {transactionTypeIds, sourceType} from "./const/filterData"
import {MatDialog} from "@angular/material/dialog";
import {OperationDetailsComponent} from "./modals/details/operation-details.component";
import {MatSelect} from "@angular/material/select";
import {ActivatedRoute, Router} from "@angular/router";
import {combineLatest, Observable, Subject} from "rxjs";
import {environment} from "../../../../../environments/environment";
import {MatSort} from "@angular/material/sort";
import {LogType} from "../../../../@core/enums/log-type.enum";
import {IUserWallet} from "../services/interfaces/user-wallet.interface";
import {WalletTypesService} from "../../../../@core/services/wallet-types.service";
import {IWalletTypeInfo} from "../../../../@core/interfaces/wallet-type-info.interface";
import {WalletTypeIdEnum} from "../../../../@core/enums/wallet-type-id.enum";

export interface RecentOperation {
  "id": number,
  "userId": number,
  "transactionId": number,
  "agentKey": string,
  "amount": number,
  "items": any,
  "data": any,
  "type": string,
  "timestamp"?: string,
  "walletTypeId"?: number | null,
  "currentBalance": number,
  "beforeBalance": number;
  "createdAt"?: string;
}

interface RecentOperationsResponse {
  operations: RecentOperation[];
  metadata: {
    amount: number;
  };
}

interface ITransactionTypeIds {
  name: string,
  id: number | number[],
  selected: boolean
}

@Component({
  selector: 'app-operations-log',
  templateUrl: './operations-log.component.html',
  styleUrls: ['./operations-log.component.scss']
})
export class OperationsLogComponent implements AfterViewInit, OnDestroy {
  @ViewChild('matSelect') matSelect: MatSelect;
  @ViewChild('matSortOperations', {read: MatSort, static: true}) matSortOperations: MatSort;

  public isGoFast: boolean = environment.projectName === 'CSGOFAST';

  recentOperationColumns: string[] = ['date', 'sourceType', 'walletType', 'type', 'amount', 'balanceStat', 'details'];
  recentOperations: MatTableDataSource<RecentOperation> = new MatTableDataSource<RecentOperation>();

  public filterForm: FormGroup = this.fb.group({
    walletTypeIds: [[WalletTypeIdEnum.REAL]],
    source: ['all'],
    keys: [null],
  });
  public selectedFilters: FormControl = new FormControl();
  public searchText: FormControl = new FormControl('');
  public transactionTypeIds: ITransactionTypeIds[] = transactionTypeIds;
  public sourceType: any = sourceType;
  public logType = LogType;
  public isLoading: boolean = true;
  public totalRows: number = 0;
  public pageSize: number = 20;
  public currentPage: number = 0;
  public readonly pageSizeOptions: number[] = [20, 50, 100];
  public readonly WalletTypeIdEnum = WalletTypeIdEnum;
  public walletTypesById$: Observable<Map<number, IWalletTypeInfo>> = this.walletTypesService.getAll().pipe(
    map(list => new Map(list.map(item => [item.id, item])))
  );
  public walletOptions$: Observable<IUserWallet[]> = combineLatest([
    this.userService.getUserWallets(),
    this.walletTypesById$,
  ]).pipe(
    map(([wallets, typesById]) => {
      const hasReal = wallets.some(wallet => wallet.walletType?.id === WalletTypeIdEnum.REAL);
      if (hasReal || !typesById.has(WalletTypeIdEnum.REAL)) {
        return wallets;
      }
      return [{
        id: 0,
        balance: 0,
        walletType: {id: WalletTypeIdEnum.REAL},
      }, ...wallets];
    })
  );
  private readonly destroyed$: Subject<void> = new Subject<void>();

  constructor(private userService: UserService,
              private fb: FormBuilder,
              private walletTypesService: WalletTypesService,
              private readonly dialog: MatDialog,
              private activatedRoute: ActivatedRoute,
              private router: Router
  ) {
    this.subscribeEmitters();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngAfterViewInit() {
    this.recentOperations.sort = this.matSortOperations;
  }

  selectedValue(): void {
    this.filterForm.patchValue({keys: this.selectedFilters.value.flat()});
    this.searchText.reset();
    this.matSelect.close();
  }

  public openDetailsModal(element: any): void {
    const params = {
      width: '60vw',
      height: 'fit-content',
      data: element
    }
    this.dialog.open(OperationDetailsComponent, params)
  }

  public pageChanged(event: PageEvent): void {
    const {source, keys, walletTypeIds} = this.filterForm.value;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        page: event.pageIndex + 1,
        pageSize: event.pageSize,
        source,
        keys: (keys ?? []).join(','),
        walletTypeIds: (walletTypeIds ?? []).join(','),
      },
    });
  }

  private subscribeEmitters(): void {
    this.filterForm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged((a, b) =>
          a.source === b.source &&
          (a.keys ?? []).join(',') === (b.keys ?? []).join(',') &&
          (a.walletTypeIds ?? []).join(',') === (b.walletTypeIds ?? []).join(',')
        ),
        takeUntil(this.destroyed$)
      )
      .subscribe(async (params: { source: string, keys: number[], walletTypeIds: number[] }) => {
          const source = params.source;
          const keys = source === 'inventory' ? [] : params.keys;
          await this.router.navigate([],
            {
              relativeTo: this.activatedRoute,
              queryParams: {
                page: 1,
                pageSize: this.pageSize,
                source,
                keys: (keys ?? []).join(','),
                walletTypeIds: (params.walletTypeIds ?? []).join(','),
              },
            }).then();

        }
      );

    this.activatedRoute.queryParamMap
      .pipe(
        takeUntil(this.destroyed$),
        tap(() => {
          this.isLoading = true;
        }),
        map((p: any) => p.params),
        switchMap((params: { page?: string, pageSize?: string, source?: string, keys?: string, walletTypeIds?: string }) => {
          const source = params.source ?? 'all';
          const pageSize = Number(params.pageSize) || 20;
          const pageNumber = Number(params.page) || 1;
          let keys = params.keys ? params.keys.split(',').map((item: string) => Number(item)) : [];
          const walletTypeIds = params.walletTypeIds == null
            ? [WalletTypeIdEnum.REAL]
            : params.walletTypeIds
              ? params.walletTypeIds.split(',').map((item: string) => Number(item))
              : [];

          this.pageSize = pageSize;
          this.currentPage = pageNumber - 1;

          if (source === 'inventory') {
            keys = [];
            this.selectedFilters.disable({emitEvent: false});
          } else {
            this.selectedFilters.enable({emitEvent: false});
          }
          this.filterForm.patchValue({source, keys, walletTypeIds}, {emitEvent: false});
          this.selectedFilters.patchValue(keys, {emitEvent: false});
          return this.userService.getUserRecentOperations(pageSize, pageNumber, source, keys, walletTypeIds);
        }))
      .subscribe((response: RecentOperationsResponse) => {
        this.isLoading = false;
        this.recentOperations.data = response.operations ?? [];
        this.totalRows = Number(response.metadata?.amount ?? 0);
      })
  }
}
